import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import type { OwnerBannerOption, StoreResponse } from '@clarity/contract';
import { useStoreBannerOptions } from '../../queries/index.js';
import { useI18n } from '../../lib/i18n.js';
import { BannerOptionRow } from './BannerOptionRow.js';
import { joinMessage } from './joinMessage.js';

/** How long the "Copied" confirmation stays up (mock: 2200 ms). */
const COPIED_MS = 2200;

/**
 * The owner "Banners you can join" tab (clarity-banner-options Req 6.2, 6.4,
 * 6.6, 7.1–7.4), reproducing the `p-banners` panel of
 * `brand/banner-options-mock.html`: the about card, then "Coming up" and
 * "Running now" groups of {@link BannerOptionRow}s in the server's order, each
 * with a How-to-join panel holding the {@link joinMessage} and Copy message.
 *
 * One How-to-join panel is open at a time (mock `S.joinOpen`). Nothing about
 * viewing, opening or copying is sent anywhere (Req 7.4): the only side effect
 * is the clipboard write.
 */
export function BannersView({ store, today }: { store: StoreResponse; today?: string }) {
  const { t, localeTag } = useI18n();
  const query = useStoreBannerOptions(store.id);
  const platform = t(`bannerOptions.platform.${store.platformKey}`);

  const [joinOpen, setJoinOpen] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (copiedTimer.current) clearTimeout(copiedTimer.current);
    },
    [],
  );

  const copy = useCallback(async (id: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // No clipboard (insecure context / denied): the message stays on screen
      // to copy by hand, so don't claim it was copied.
      return;
    }
    if (copiedTimer.current) clearTimeout(copiedTimer.current);
    setCopied(id);
    copiedTimer.current = setTimeout(() => setCopied(null), COPIED_MS);
  }, []);

  const options = query.data?.status === 200 ? query.data.body.options : null;

  let body: ReactNode;
  if (query.isLoading) {
    body = <BannersLoading label={t('banners.loading')} />;
  } else if (!options) {
    body = (
      <div className="banners-view__alert" role="alert">
        <AlertIcon />
        <span>
          <b>{t('banners.error.title')}</b> {t('banners.error.body')}
        </span>
        <button type="button" className="ghost" onClick={() => void query.refetch()}>
          {t('banners.retry')}
        </button>
      </div>
    );
  } else if (options.length === 0) {
    body = (
      <div className="card empty banners-view__empty" role="status">
        <EmptyIcon />
        <div className="empty__title">{t('banners.empty.title', { platform })}</div>
        <p className="empty__sub">{t('banners.empty.body', { platform })}</p>
      </div>
    );
  } else {
    const upcoming = options.filter((o) => o.group === 'upcoming');
    const running = options.filter((o) => o.group === 'running');
    const rows = (list: OwnerBannerOption[]) => (
      <div className="card banners-view__card">
        <ul className="banner-list">
          {list.map((o) => {
            const message = joinMessage(o, store, t, localeTag);
            return (
              <BannerOptionRow
                key={o.id}
                option={o}
                {...(today !== undefined ? { today } : {})}
                join={{
                  open: joinOpen === o.id,
                  onToggle: () => setJoinOpen((cur) => (cur === o.id ? null : o.id)),
                  message,
                  onCopy: () => void copy(o.id, message),
                  copied: copied === o.id,
                }}
              />
            );
          })}
        </ul>
      </div>
    );
    body = (
      <>
        {upcoming.length > 0 && (
          <section aria-labelledby="banners-group-upcoming">
            <h2 className="banners-view__group-h" id="banners-group-upcoming">
              {t('banners.group.upcoming')}{' '}
              <small>
                {t(
                  upcoming.length === 1
                    ? 'banners.group.upcoming.count.one'
                    : 'banners.group.upcoming.count.many',
                  { count: upcoming.length },
                )}
              </small>
            </h2>
            {rows(upcoming)}
          </section>
        )}
        {running.length > 0 && (
          <section aria-labelledby="banners-group-running">
            <h2 className="banners-view__group-h" id="banners-group-running">
              {t('banners.group.running')} <small>{t('banners.group.running.sub')}</small>
            </h2>
            {rows(running)}
          </section>
        )}
      </>
    );
  }

  return (
    <div className="banners-view">
      <div className="card banners-view__about">
        <InfoIcon />
        <p>
          {t('banners.about.offering', { platform })} <b>{t('banners.about.noSignup')}</b>
          {t('banners.about.ask', { platform })} {t('banners.about.fees', { platform })}
        </p>
      </div>
      {body}
    </div>
  );
}

/** Two skeleton rows in the row grid, plus an announced status (mock loading state). */
function BannersLoading({ label }: { label: string }) {
  return (
    <>
      <div className="card banners-view__card" aria-busy="true">
        <ul className="banner-list" aria-hidden="true">
          {[0, 1].map((i) => (
            <li className="banner-row" key={i}>
              <div className="banner-row__when" />
              <div className="banners-view__sk-body">
                <span className="sk" style={{ inlineSize: '42%', blockSize: '16px' }} />
                <span className="sk" style={{ inlineSize: '64%' }} />
                <span className="sk" style={{ inlineSize: '36%' }} />
              </div>
              <div className="banner-row__act">
                <span className="sk" style={{ inlineSize: '90px', blockSize: '20px' }} />
              </div>
            </li>
          ))}
        </ul>
      </div>
      <span className="sr-only" role="status">
        {label}
      </span>
    </>
  );
}

// ── Icons (the mock's stroke set) ──────────────────────────────────────────────

function ViewIcon({ className, children }: { className: string; children: ReactNode }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

function InfoIcon() {
  return (
    <ViewIcon className="banners-view__i">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5M12 8h.01" />
    </ViewIcon>
  );
}

function AlertIcon() {
  return (
    <ViewIcon className="banners-view__i">
      <path d="M12 4 2.5 20h19z" />
      <path d="M12 10v4M12 17h.01" />
    </ViewIcon>
  );
}

function EmptyIcon() {
  return (
    <ViewIcon className="empty__glyph">
      <rect x="3" y="5" width="18" height="10" rx="2" />
      <path d="M7 19h10M12 15v4" />
      <path d="m9 8 6 4M15 8l-6 4" />
    </ViewIcon>
  );
}

/** The megaphone glyph: the Banners tab icon and the new-options notice. */
export function MegaphoneIcon({ className }: { className: string }) {
  return (
    <ViewIcon className={className}>
      <path d="M3 11h3l9-5v12l-9-5H3z" />
      <path d="M6 13v4a2 2 0 0 0 2 2h1" />
      <path d="M19 9a4 4 0 0 1 0 6" />
    </ViewIcon>
  );
}
